import type { CustomerFieldMappingTarget } from '@/contracts/customer-application-binding'
import type { ContractSourceMeta } from '@/contracts/questionnaire'
import { DEFAULT_SHARE_MAX_RESPONSES } from '@/contracts/share'
import {
  MIAOLIN_CUSTOMER_ONBOARDING_FORM_ID,
  MIAOLIN_LEGACY_CUSTOMER_ONBOARDING_FORM_IDS,
  normalizeMiaolinCustomerOnboardingFormId,
} from '@/lib/miaolin-form-ids'
import { GCIS_TAX_ID_LOOKUP_SOURCE_LABEL } from '@/lib/miaolin-tax-id-lookup'
import {
  TAIWAN_LANDLINE_EXTENSION_PRESET,
  TAIWAN_LANDLINE_EXTENSION_REGEX,
  TAIWAN_MOBILE_PRESET,
  TAIWAN_MOBILE_REGEX,
} from '@/lib/phone-validation'
import { SHARED_WORKSPACE_ID } from '@/lib/shared-workspace'
import {
  TAIWAN_ADDRESS_MODE,
  TAIWAN_ADDRESS_POSTAL_CODE_PRECISION,
  type TaiwanAddressAnswer,
} from '@/lib/taiwan-address'
import type {
  DashboardStats,
  Form,
  LogicDisplayTarget,
  LogicJump,
  PublicForm,
  Question,
  QuestionSettings,
  QuestionType,
  ResponseSummary,
} from '@/types/form'

export const mockWorkspaceId = SHARED_WORKSPACE_ID
export const mockUserId = '22222222-2222-4222-8222-222222222222'
export const primaryFormId = MIAOLIN_CUSTOMER_ONBOARDING_FORM_ID
export const legacyPrimaryFormIds = MIAOLIN_LEGACY_CUSTOMER_ONBOARDING_FORM_IDS
export const limitedFormId = '33333333-3333-4333-8333-333333333336'
export const miaolinDemoTitle = '苗林實業客戶資料建檔'
export const advancedPreviewGroupId = 'qgt-miaolin-workflow'

export const mockDataMeta: ContractSourceMeta = {
  source: 'synthetic',
  permissionScope: 'mock-local',
  dataDate: '2026-05-26',
  confidence: 1,
}

export const defaultTheme = {
  backgroundColor: '#F8FAFC',
  textColor: '#111827',
  answerColor: '#334155',
  buttonColor: '#0F766E',
  buttonTextColor: '#FFFFFF',
  accentColor: '#D97706',
  radius: 8,
  maxWidth: 760,
  fontFamily: 'Geist',
  headingFontFamily: 'Geist',
  bodyFontFamily: 'Noto Sans TC',
  fontScale: 'comfortable' as const,
  lineHeight: 1.55,
  progressStyle: 'thin' as const,
}

export const miaolinQuestionGroupDefinitions = [
  {
    id: 'qgt-miaolin-workflow',
    title: '流程控制',
    description: '判斷是全新客戶建檔或既有客戶資料變更，並保留客戶代號與來源。',
    category: '苗林客戶建檔',
    tags: ['苗林', '建檔類型', '資料變更'],
    orderIndex: 0,
  },
  {
    id: 'qgt-miaolin-source-profile',
    title: '客戶來源與基本資料',
    description: '新客來源、客戶簡稱、負責人、統一編號與統編抬頭。',
    category: '苗林客戶建檔',
    tags: ['苗林', '客戶資料', '統編'],
    orderIndex: 1,
  },
  {
    id: 'qgt-miaolin-contact',
    title: '聯絡資訊',
    description: '聯絡人、電話、傳真與 Email。',
    category: '苗林客戶建檔',
    tags: ['苗林', '聯絡資訊', 'Email'],
    orderIndex: 2,
  },
  {
    id: 'qgt-miaolin-payment-scale',
    title: '付款與規模',
    description: '員工人數。',
    category: '苗林客戶建檔',
    tags: ['苗林', '員工人數'],
    orderIndex: 3,
  },
  {
    id: 'qgt-miaolin-address',
    title: '地址資訊',
    description: '送貨、發票與登記地址。',
    category: '苗林客戶建檔',
    tags: ['苗林', '地址'],
    orderIndex: 4,
  },
  {
    id: 'qgt-miaolin-billing-receiving',
    title: '帳務與收貨',
    description: '帳單收件、可收貨時間與營業時段。',
    category: '苗林客戶建檔',
    tags: ['苗林', '帳務', '收貨'],
    orderIndex: 5,
  },
  {
    id: 'qgt-miaolin-sales-ownership',
    title: '部門與業務歸屬',
    description: '部門別與各部門負責業務。',
    category: '苗林客戶建檔',
    tags: ['苗林', '業務', '部門'],
    orderIndex: 6,
  },
  {
    id: 'qgt-miaolin-customer-classification',
    title: '客戶分類',
    description: '客戶類別、通路別與型態別。',
    category: '苗林客戶建檔',
    tags: ['苗林', '通路', '型態'],
    orderIndex: 7,
  },
  {
    id: 'qgt-miaolin-region',
    title: '區域資訊',
    description: '客戶縣市區域與主要直轄市行政區。',
    category: '苗林客戶建檔',
    tags: ['苗林', '區域', '行政區'],
    orderIndex: 8,
  },
  {
    id: 'qgt-miaolin-notes-tracking',
    title: '其他欄位',
    description: '備註、填單人員與食品業者登入字號。',
    category: '苗林客戶建檔',
    tags: ['苗林', '備註', '追蹤'],
    orderIndex: 9,
  },
] as const

function choice(questionId: string, label: string, orderIndex: number) {
  return {
    id: `${questionId}-choice-${orderIndex + 1}`,
    questionId,
    label,
    value: label,
    orderIndex,
  }
}

function miaolinQuestionId(no: number) {
  return `q-miaolin-${String(no).padStart(2, '0')}`
}

function taiwanAddressAnswer(
  city: string,
  district: string,
  postalCode: string,
  detail: string,
): TaiwanAddressAnswer {
  return { city, district, postalCode, detail }
}

type MiaolinConditionalDisplayConditionInput = {
  controllerNo: number
  controllerValue?: string
  mode?: 'show_when_equals' | 'show_when_answered' | 'show_when_not_answered'
}

function conditionalDisplayGroups(
  conditionGroups: MiaolinConditionalDisplayConditionInput[][],
  source = 'question-target-logic',
): QuestionSettings {
  const groups = conditionGroups.map((group) =>
    group.map((condition) => ({
      controllerQuestionId: miaolinQuestionId(condition.controllerNo),
      ...(condition.controllerValue ? { controllerValue: condition.controllerValue } : {}),
      mode: condition.mode ?? 'show_when_equals',
      source,
    })),
  )
  const first = groups[0]?.[0]
  return {
    conditionalDisplay: {
      ...(first ?? { controllerQuestionId: miaolinQuestionId(1), mode: 'show_when_answered' }),
      source,
      conditionGroups: groups,
    },
  }
}

function workflowChoiceCondition(value: '全新客戶' | '既有客戶變更資料') {
  return { controllerNo: 1, controllerValue: value }
}

function existingChangeTypeCondition(value: string) {
  return { controllerNo: 2, controllerValue: value }
}

function showWhenExistingCustomerChangeSelected(): QuestionSettings {
  return conditionalDisplayGroups([
    [workflowChoiceCondition('既有客戶變更資料'), { controllerNo: 2, mode: 'show_when_answered' }],
  ])
}

function showWhenWorkflowAnswered(): QuestionSettings {
  return conditionalDisplayGroups([[{ controllerNo: 1, mode: 'show_when_answered' }]])
}

function showWhenNewCustomer(): QuestionSettings {
  return conditionalDisplayGroups([[workflowChoiceCondition('全新客戶')]])
}

function showWhenExistingCustomerChange(): QuestionSettings {
  return conditionalDisplayGroups([[workflowChoiceCondition('既有客戶變更資料')]])
}

function showWhenNewCustomerOrExistingChangeTypes(changeTypes: string[]): QuestionSettings {
  return conditionalDisplayGroups([
    [workflowChoiceCondition('全新客戶')],
    ...changeTypes.map((changeType) => [
      workflowChoiceCondition('既有客戶變更資料'),
      existingChangeTypeCondition(changeType),
    ]),
  ])
}

function showWhenNewCustomerAndEquals(
  controllerNo: number,
  controllerValue: string,
): QuestionSettings {
  return conditionalDisplayGroups([
    [workflowChoiceCondition('全新客戶'), { controllerNo, controllerValue }],
  ])
}

function showTaxTitleWhenVisible(): QuestionSettings {
  return conditionalDisplayGroups([
    [workflowChoiceCondition('全新客戶'), { controllerNo: 14, mode: 'show_when_answered' }],
    [
      workflowChoiceCondition('既有客戶變更資料'),
      existingChangeTypeCondition('既有客戶變更統編'),
      { controllerNo: 14, mode: 'show_when_answered' },
    ],
  ])
}

function showNoTaxReasonWhenVisible(): QuestionSettings {
  return conditionalDisplayGroups([
    [workflowChoiceCondition('全新客戶'), { controllerNo: 14, mode: 'show_when_not_answered' }],
    [
      workflowChoiceCondition('既有客戶變更資料'),
      existingChangeTypeCondition('既有客戶變更統編'),
      { controllerNo: 14, mode: 'show_when_not_answered' },
    ],
  ])
}

function question(params: {
  no: number
  id?: string
  groupId: (typeof miaolinQuestionGroupDefinitions)[number]['id']
  type: QuestionType
  title: string
  description?: string
  required?: boolean
  specRequired?: boolean
  customerFieldTarget?: CustomerFieldMappingTarget
  dbFieldName?: string
  dataType?: string
  settings?: QuestionSettings
  choices?: string[]
  orderIndex?: number
}): Question {
  const id = params.id ?? miaolinQuestionId(params.no)
  const specSettings: QuestionSettings = {
    showQuestionNumber: true,
    specQuestionNo: params.no,
    specRequired: params.specRequired ?? params.required ?? true,
    ...(params.customerFieldTarget ? { customerFieldTarget: params.customerFieldTarget } : {}),
    ...(params.dbFieldName ? { dbFieldName: params.dbFieldName } : {}),
    ...(params.dataType ? { dataType: params.dataType } : {}),
  }
  return {
    id,
    formId: primaryFormId,
    groupId: params.groupId,
    type: params.type,
    title: params.title,
    description: params.description,
    required: params.required ?? true,
    settings: { ...specSettings, ...(params.settings ?? {}) },
    choices: (params.choices ?? []).map((label, index) => choice(id, label, index)),
    orderIndex: params.orderIndex ?? params.no - 1,
  }
}

const sourceChoices = [
  '501 陌生開發',
  '502 客戶引薦',
  '503 FaceBook',
  '504 原始客戶新品牌',
  '505 網路搜尋',
  '506 舊識',
  '507 主動來電',
  '508 原客戶展店',
  '515 苗林官網',
]

const customerChannelChoices = [
  '112 飯店業',
  '113 餐廳',
  '114 日式餐飲',
  '119 製造商',
  '123 公家/教學單位',
  '126 業外公司',
  '130 烘焙業',
  '131 飲品咖啡店',
  '132 業內合作夥伴/經銷商',
  '133 通路與零售商',
]

const customerTypeChoices = [
  '211 西式麵包店',
  '212 西式甜點店',
  '214 商務旅館',
  '215 民宿',
  '216 西式餐廳',
  '217 韓式餐廳',
  '218 中式餐廳',
  '219 拉麵店',
  '220 烏龍麵店',
  '223 鐵板燒',
  '224 咖啡店',
  '225 中式麵點',
  '226 中式糕點',
  '227 製麵廠',
  '228 盤商',
  '229 烘焙材料行',
  '230 個人工作室',
  '233 代工廠',
  '234 同業往來',
  '235 傳統市場',
  '237 壽司店',
  '238 鍋物',
  '239 早餐店',
  '240 早午餐',
  '243 雜貨商',
  '244 講習會',
  '245 公家機關',
  '246 學校',
  '247 農場教育',
  '248 豆製所',
  '249 飲料店',
  '250 冰店',
  '251 小吃',
  '252 親子餐廳',
  '254 合作夥伴',
  '255 一般公司行號',
  '256 泰式料理',
  '257 烘焙教室',
  '258 料理教室',
  '261 Buffet自助餐',
  '262 超市',
  '264 物流/檢驗',
  '265 丼飯店',
  '266 便利商店',
  '267 越式料理',
  '268 蔬食餐廳',
  '270 團購主',
  '271 餐酒館/酒吧',
  '272 披薩店',
  '273 團膳',
  '274 量販店',
  '275 鮮食廠',
  '276 精肉舖',
  '277 電商平台',
  '278 星級/觀光飯店',
  '279 居酒屋/燒烤店',
  '280 豬排店',
  '281 和食餐廳',
  '282 咖哩飯店',
  '283 日式鐵板燒',
  '284 日式鍋物',
  '285 伴手禮',
  '286 燒肉店',
  '287 加工食品製造廠',
]

const customerChannelTypeChoices = {
  '130 烘焙業': [
    '211 西式麵包店',
    '212 西式甜點店',
    '225 中式麵點',
    '226 中式糕點',
    '230 個人工作室',
    '285 伴手禮',
  ],
  '112 飯店業': ['214 商務旅館', '215 民宿', '278 星級/觀光飯店'],
  '113 餐廳': [
    '216 西式餐廳',
    '217 韓式餐廳',
    '218 中式餐廳',
    '223 鐵板燒',
    '238 鍋物',
    '239 早餐店',
    '240 早午餐',
    '251 小吃',
    '252 親子餐廳',
    '256 泰式料理',
    '261 Buffet自助餐',
    '267 越式料理',
    '268 蔬食餐廳',
    '271 餐酒館/酒吧',
    '272 披薩店',
  ],
  '114 日式餐飲': [
    '219 拉麵店',
    '220 烏龍麵店',
    '237 壽司店',
    '265 丼飯店',
    '279 居酒屋/燒烤店',
    '280 豬排店',
    '281 和食餐廳',
    '282 咖哩飯店',
    '283 日式鐵板燒',
    '284 日式鍋物',
    '286 燒肉店',
  ],
  '119 製造商': ['227 製麵廠', '233 代工廠', '273 團膳', '275 鮮食廠', '287 加工食品製造廠'],
  '123 公家/教學單位': [
    '244 講習會',
    '245 公家機關',
    '246 學校',
    '247 農場教育',
    '257 烘焙教室',
    '258 料理教室',
  ],
  '126 業外公司': ['255 一般公司行號', '264 物流/檢驗'],
  '131 飲品咖啡店': ['224 咖啡店', '248 豆製所', '249 飲料店', '250 冰店'],
  '132 業內合作夥伴/經銷商': ['228 盤商', '234 同業往來', '254 合作夥伴'],
  '133 通路與零售商': [
    '229 烘焙材料行',
    '235 傳統市場',
    '243 雜貨商',
    '262 超市',
    '266 便利商店',
    '270 團購主',
    '274 量販店',
    '276 精肉舖',
    '277 電商平台',
  ],
} satisfies Record<string, string[]>

const customerChannelTypeFallbackChoices = Array.from(
  new Set(Object.values(customerChannelTypeChoices).flat()),
)

const cityChoices = [
  '宜蘭縣',
  '花蓮縣',
  '南投縣',
  '屏東縣',
  '苗栗縣',
  '桃園市',
  '高雄市',
  '臺北市',
  '基隆市',
  '新北市',
  '新竹縣市',
  '嘉義縣',
  '嘉義縣市',
  '彰化縣',
  '臺中市',
  '臺東縣',
  '臺南市',
  '雲林縣',
]

const taipeiDistrictChoices = [
  '中正區',
  '大同區',
  '中山區',
  '松山區',
  '大安區',
  '萬華區',
  '信義區',
  '士林區',
  '北投區',
  '內湖區',
  '南港區',
  '文山區',
]

const newTaipeiDistrictChoices = [
  '板橋區',
  '新莊區',
  '中和區',
  '永和區',
  '土城區',
  '樹林區',
  '三峽區',
  '鶯歌區',
  '三重區',
  '蘆洲區',
  '五股區',
  '泰山區',
  '林口區',
  '八里區',
  '淡水區',
  '三芝區',
  '石門區',
  '金山區',
  '萬里區',
  '汐止區',
  '瑞芳區',
  '貢寮區',
  '平溪區',
  '雙溪區',
  '新店區',
  '深坑區',
  '石碇區',
  '坪林區',
  '烏來區',
]

const taichungDistrictChoices = [
  '中區',
  '東區',
  '南區',
  '西區',
  '北區',
  '北屯區',
  '西屯區',
  '南屯區',
  '太平區',
  '大里區',
  '霧峰區',
  '烏日區',
  '豐原區',
  '后里區',
  '石岡區',
  '東勢區',
  '新社區',
  '潭子區',
  '大雅區',
  '神岡區',
  '大肚區',
  '沙鹿區',
  '龍井區',
  '梧棲區',
  '清水區',
  '大甲區',
  '外埔區',
  '大安區',
  '和平區',
]

const tainanDistrictChoices = [
  '中西區',
  '東區',
  '南區',
  '北區',
  '安平區',
  '安南區',
  '永康區',
  '歸仁區',
  '新化區',
  '左鎮區',
  '玉井區',
  '楠西區',
  '南化區',
  '仁德區',
  '關廟區',
  '龍崎區',
  '官田區',
  '麻豆區',
  '佳里區',
  '西港區',
  '七股區',
  '將軍區',
  '學甲區',
  '北門區',
  '新營區',
  '後壁區',
  '白河區',
  '東山區',
  '六甲區',
  '下營區',
  '柳營區',
  '鹽水區',
  '善化區',
  '大內區',
  '山上區',
  '新市區',
  '安定區',
]

const kaohsiungDistrictChoices = [
  '楠梓區',
  '左營區',
  '鼓山區',
  '三民區',
  '鹽埕區',
  '前金區',
  '新興區',
  '苓雅區',
  '前鎮區',
  '旗津區',
  '小港區',
  '鳳山區',
  '大寮區',
  '鳥松區',
  '林園區',
  '仁武區',
  '大樹區',
  '大社區',
  '岡山區',
  '路竹區',
  '橋頭區',
  '梓官區',
  '彌陀區',
  '永安區',
  '燕巢區',
  '田寮區',
  '阿蓮區',
  '茄萣區',
  '湖內區',
  '旗山區',
  '美濃區',
  '內門區',
  '杉林區',
  '甲仙區',
  '六龜區',
  '茂林區',
  '桃源區',
  '那瑪夏區',
]

export const sampleQuestions: Question[] = [
  question({
    no: 1,
    groupId: 'qgt-miaolin-workflow',
    type: 'MULTIPLE_CHOICE',
    title: '新客戶建檔選擇',
    customerFieldTarget: 'formType',
    dbFieldName: 'form_type',
    dataType: 'ENUM',
    choices: ['全新客戶', '既有客戶變更資料'],
    settings: { autoAdvance: true, defaultValue: '全新客戶' },
  }),
  question({
    no: 2,
    groupId: 'qgt-miaolin-workflow',
    type: 'MULTIPLE_CHOICE',
    title: '資料變更選擇',
    specRequired: true,
    customerFieldTarget: 'changeType',
    dbFieldName: 'change_type',
    dataType: 'ENUM',
    choices: [
      '既有客戶變更電話',
      '既有客戶變更統編',
      '既有客戶變更送貨地址',
      '既有客戶變更送貨時段',
    ],
    settings: {
      ...showWhenExistingCustomerChange(),
      autoAdvance: true,
      conditionalRequired: '建檔選擇為既有客戶變更資料時必填',
    },
  }),
  question({
    no: 3,
    groupId: 'qgt-miaolin-workflow',
    type: 'SHORT_TEXT',
    title: '既有客戶代號',
    specRequired: true,
    customerFieldTarget: 'existingCustomerCode',
    dbFieldName: 'existing_customer_code',
    dataType: 'VARCHAR(50)',
    description: 'Ex: G1858。若 webhook 已設定，可按比對確認既有客戶代號是否存在。',
    settings: {
      ...showWhenExistingCustomerChangeSelected(),
      placeholder: 'G1858',
      conditionalRequired: '既有客戶變更資料時必填',
      customerDuplicateCheck: {
        enabled: true,
        providerMode: 'webhook',
        field: 'existing_customer_code',
        purpose: 'existing_lookup',
        sourceLabel: 'Miaolin existing customer check webhook',
      },
      existingCustomerNameTargetQuestionId: miaolinQuestionId(5),
    },
  }),
  question({
    no: 5,
    groupId: 'qgt-miaolin-workflow',
    type: 'SHORT_TEXT',
    title: '客戶簡稱',
    required: false,
    specRequired: false,
    customerFieldTarget: 'customerShortName',
    dbFieldName: 'customer_short_name',
    dataType: 'NVARCHAR(100)',
    settings: {
      ...showWhenExistingCustomerChangeSelected(),
      placeholder: '由既有客戶代號比對自動帶入',
      readOnly: true,
      readOnlyReason: '此欄位由既有客戶代號比對結果帶入；若要修正，請先修正既有客戶代號後重新比對。',
      autofillSourceQuestionId: miaolinQuestionId(3),
      customerShortNameSource: 'existing_customer_lookup',
    },
  }),
  question({
    no: 4,
    groupId: 'qgt-miaolin-source-profile',
    type: 'DROPDOWN',
    title: '新客戶來源',
    customerFieldTarget: 'customerSource',
    dbFieldName: 'customer_source',
    dataType: 'VARCHAR(10)',
    choices: sourceChoices,
    settings: { ...showWhenNewCustomer(), placeholder: '請選擇新客戶來源' },
  }),
  question({
    no: 6,
    groupId: 'qgt-miaolin-source-profile',
    type: 'SHORT_TEXT',
    title: '客戶簡稱',
    customerFieldTarget: 'customerShortName',
    dbFieldName: 'customer_short_name',
    dataType: 'NVARCHAR(100)',
    settings: {
      ...showWhenNewCustomer(),
      placeholder: '請輸入客戶簡稱',
      customerDuplicateCheck: {
        enabled: true,
        providerMode: 'webhook',
        field: 'customer_short_name',
        sourceLabel: 'Miaolin customer check webhook',
      },
    },
  }),
  question({
    no: 8,
    groupId: 'qgt-miaolin-source-profile',
    type: 'SHORT_TEXT',
    title: '負責人',
    customerFieldTarget: 'ownerName',
    dbFieldName: 'owner_name',
    dataType: 'NVARCHAR(50)',
    description: '請填寫全名，非登不可需要使用到；不要用英文名稱。',
    settings: { ...showWhenNewCustomer(), placeholder: '負責人全名' },
  }),
  question({
    no: 9,
    groupId: 'qgt-miaolin-contact',
    type: 'SHORT_TEXT',
    title: '聯絡人',
    customerFieldTarget: 'contactName',
    dbFieldName: 'contact_name',
    dataType: 'NVARCHAR(50)',
    description: '請填寫全名，非登不可需要使用到；不要用英文名稱。',
    settings: { ...showWhenNewCustomer(), placeholder: '聯絡人全名' },
  }),
  question({
    no: 10,
    groupId: 'qgt-miaolin-contact',
    type: 'TAIWAN_LANDLINE',
    title: '市話',
    customerFieldTarget: 'phone1',
    dbFieldName: 'phone_1',
    dataType: 'VARCHAR(30)',
    description:
      '請先選擇區碼，再填寫市話號碼；區碼和號碼中間請用 "-" 隔開，可加分機。EX: 02-26589848#123。',
    settings: {
      ...showWhenNewCustomerOrExistingChangeTypes(['既有客戶變更電話']),
      placeholder: '26589848#123',
      validation: 'phone',
      validationPreset: TAIWAN_LANDLINE_EXTENSION_PRESET,
      validationRegex: TAIWAN_LANDLINE_EXTENSION_REGEX,
      validationTestValue: '02-26589848#123',
      taiwanLandlineAreaCode: '02',
    },
  }),
  question({
    no: 11,
    groupId: 'qgt-miaolin-contact',
    type: 'TAIWAN_MOBILE',
    title: '手機',
    description: '請填台灣手機號碼，可用本地格式或國碼格式。EX: 0912345678、+886912345678。',
    required: false,
    customerFieldTarget: 'phone2',
    dbFieldName: 'phone_2',
    dataType: 'VARCHAR(30)',
    settings: {
      ...showWhenNewCustomerOrExistingChangeTypes(['既有客戶變更電話']),
      placeholder: '0912345678',
      validation: 'phone',
      validationPreset: TAIWAN_MOBILE_PRESET,
      validationRegex: TAIWAN_MOBILE_REGEX,
      validationTestValue: '0912345678',
    },
  }),
  question({
    no: 12,
    groupId: 'qgt-miaolin-contact',
    type: 'TAIWAN_LANDLINE',
    title: '傳真號碼',
    description: '中間請用 "-" 隔開；EX: 02-26589848。',
    required: false,
    customerFieldTarget: 'fax',
    dbFieldName: 'fax',
    dataType: 'VARCHAR(30)',
    settings: {
      ...showWhenNewCustomer(),
      placeholder: '26589848',
      validation: 'phone',
      validationPreset: TAIWAN_LANDLINE_EXTENSION_PRESET,
      validationRegex: TAIWAN_LANDLINE_EXTENSION_REGEX,
      validationTestValue: '02-26589848',
      taiwanLandlineAreaCode: '02',
    },
  }),
  question({
    no: 13,
    groupId: 'qgt-miaolin-contact',
    type: 'EMAIL',
    title: 'E-Mail',
    description: 'B2B系統線上下單設定使用。',
    required: false,
    customerFieldTarget: 'email',
    dbFieldName: 'email',
    dataType: 'VARCHAR(100)',
    settings: {
      ...showWhenNewCustomer(),
      placeholder: 'customer@example.com',
      validation: 'email',
      maxLength: 100,
    },
  }),
  question({
    no: 14,
    groupId: 'qgt-miaolin-source-profile',
    type: 'SHORT_TEXT',
    title: '統一編號',
    description: '請輸入 8 碼統一編號；匯出 CSV / Excel 時會以文字格式保留開頭 0。',
    required: false,
    customerFieldTarget: 'taxId',
    dbFieldName: 'tax_id',
    dataType: 'VARCHAR(20)',
    settings: {
      ...showWhenNewCustomerOrExistingChangeTypes(['既有客戶變更統編']),
      placeholder: '01234567',
      validation: 'number',
      validationRegex: '^\\d{8}$',
      validationTestValue: '01234567',
      exportValueType: 'text',
      preserveLeadingZeros: true,
      taxIdLookup: {
        enabled: true,
        providerMode: 'gcis_open_data',
        sourceLabel: GCIS_TAX_ID_LOOKUP_SOURCE_LABEL,
        targetQuestionId: miaolinQuestionId(7),
      },
    },
  }),
  question({
    no: 44,
    groupId: 'qgt-miaolin-source-profile',
    type: 'LONG_TEXT',
    title: '沒有統編的原因',
    required: true,
    dbFieldName: 'no_tax_id_reason',
    dataType: 'NVARCHAR(300)',
    settings: {
      ...showNoTaxReasonWhenVisible(),
      placeholder: '請說明沒有統編的原因',
    },
    orderIndex: 14,
  }),
  question({
    no: 7,
    groupId: 'qgt-miaolin-source-profile',
    type: 'SHORT_TEXT',
    title: '統編抬頭（公司全名）',
    required: false,
    customerFieldTarget: 'companyFullName',
    dbFieldName: 'company_full_name',
    dataType: 'NVARCHAR(200)',
    settings: {
      ...showTaxTitleWhenVisible(),
      placeholder: '由統編比對自動帶入',
      readOnly: true,
      readOnlyReason: '此欄位由統一編號比對結果帶入；若要修正，請先修正統一編號後重新比對。',
      autofillSourceQuestionId: miaolinQuestionId(14),
    },
    orderIndex: 15,
  }),
  question({
    no: 17,
    groupId: 'qgt-miaolin-payment-scale',
    type: 'MULTIPLE_CHOICE',
    title: '員工人數',
    customerFieldTarget: 'employeeCountRange',
    dbFieldName: 'employee_count_range',
    dataType: 'ENUM',
    choices: ['1-5人', '6-20人', '21-50人', '51-100人', '100人以上'],
    settings: {
      ...showWhenNewCustomer(),
      dynamicOptions: {
        kind: 'miaolin_team_sales_users',
        providerMode: 'webhook',
        sourceLabel: 'Miaolin department sales users webhook',
        fallback: 'static_question_choices',
      },
    },
  }),
  question({
    no: 18,
    groupId: 'qgt-miaolin-address',
    type: 'TAIWAN_ADDRESS',
    title: '送貨地址',
    customerFieldTarget: 'deliveryAddress',
    dbFieldName: 'delivery_address',
    dataType: 'NVARCHAR(300)',
    settings: {
      ...showWhenNewCustomerOrExistingChangeTypes(['既有客戶變更送貨地址']),
      addressMode: TAIWAN_ADDRESS_MODE,
      postalCodePrecision: TAIWAN_ADDRESS_POSTAL_CODE_PRECISION,
      detailPlaceholder: '請輸入送貨詳細地址',
    },
  }),
  question({
    no: 19,
    groupId: 'qgt-miaolin-address',
    type: 'TAIWAN_ADDRESS',
    title: '發票地址',
    customerFieldTarget: 'invoiceAddress',
    dbFieldName: 'invoice_address',
    dataType: 'NVARCHAR(300)',
    settings: {
      ...showWhenNewCustomer(),
      addressMode: TAIWAN_ADDRESS_MODE,
      postalCodePrecision: TAIWAN_ADDRESS_POSTAL_CODE_PRECISION,
      detailPlaceholder: '請輸入發票詳細地址',
      addressDefaultSourceMode: 'manual',
      addressSourceOptions: [
        {
          value: 'same_as_delivery_address',
          label: '同送貨地址',
          sourceQuestionId: 'q-miaolin-18',
          sourceQuestionTitle: '送貨地址',
        },
        { value: 'manual', label: '其他' },
      ],
    },
  }),
  question({
    no: 20,
    groupId: 'qgt-miaolin-address',
    type: 'TAIWAN_ADDRESS',
    title: '登記地址',
    customerFieldTarget: 'registeredAddress',
    dbFieldName: 'registered_address',
    dataType: 'NVARCHAR(300)',
    settings: {
      ...showWhenNewCustomer(),
      addressMode: TAIWAN_ADDRESS_MODE,
      postalCodePrecision: TAIWAN_ADDRESS_POSTAL_CODE_PRECISION,
      detailPlaceholder: '請輸入登記詳細地址',
      addressDefaultSourceMode: 'manual',
      addressSourceOptions: [
        {
          value: 'same_as_delivery_address',
          label: '同送貨地址',
          sourceQuestionId: 'q-miaolin-18',
          sourceQuestionTitle: '送貨地址',
        },
        {
          value: 'same_as_invoice_address',
          label: '同發票地址',
          sourceQuestionId: 'q-miaolin-19',
          sourceQuestionTitle: '發票地址',
        },
        { value: 'manual', label: '其他' },
      ],
    },
  }),
  question({
    no: 21,
    groupId: 'qgt-miaolin-billing-receiving',
    type: 'EMAIL',
    title: '帳單收件Mail',
    description: '會計端寄送對帳單及發票。',
    required: false,
    customerFieldTarget: 'billingEmail',
    dbFieldName: 'billing_email',
    dataType: 'VARCHAR(100)',
    settings: {
      ...showWhenNewCustomer(),
      placeholder: 'billing@example.com',
      validation: 'email',
      maxLength: 100,
    },
  }),
  question({
    no: 22,
    groupId: 'qgt-miaolin-billing-receiving',
    type: 'SHORT_TEXT',
    title: '可收貨時間',
    customerFieldTarget: 'deliveryTimeNote',
    dbFieldName: 'delivery_time_note',
    dataType: 'NVARCHAR(200)',
    description: '請註記中午是否可以收貨。',
    settings: {
      ...showWhenNewCustomerOrExistingChangeTypes(['既有客戶變更送貨時段']),
      placeholder: '例：週一至週五 09:00-17:00，中午可收貨',
    },
  }),
  question({
    no: 23,
    groupId: 'qgt-miaolin-billing-receiving',
    type: 'SHORT_TEXT',
    title: '營業時段、公休日',
    required: false,
    customerFieldTarget: 'businessHours',
    dbFieldName: 'business_hours',
    dataType: 'NVARCHAR(200)',
    settings: {
      ...showWhenNewCustomerOrExistingChangeTypes(['既有客戶變更送貨時段']),
      placeholder: '例：10:00-20:00，週日公休',
    },
  }),
  question({
    no: 24,
    groupId: 'qgt-miaolin-sales-ownership',
    type: 'DROPDOWN',
    title: '部門別',
    customerFieldTarget: 'department',
    dbFieldName: 'department',
    dataType: 'VARCHAR(10)',
    choices: [
      '特販一課 102',
      '特通一課 109',
      '營業一課 103',
      '營業二課 106',
      '營業三課 105',
      '營業四課 111',
      '苗實 104',
    ],
    settings: { ...showWhenNewCustomer() },
  }),
  question({
    no: 25,
    groupId: 'qgt-miaolin-sales-ownership',
    type: 'DROPDOWN',
    title: '特販業務',
    required: false,
    specRequired: true,
    customerFieldTarget: 'salesRepSpecial',
    dbFieldName: 'sales_rep_special',
    dataType: 'VARCHAR(20)',
    choices: ['張家瑞 136', '廖玉媗 T285', '張皓茹 T356'],
    settings: {
      ...showWhenNewCustomerAndEquals(24, '特販一課 102'),
      conditionalRequired: '部門別為特販一課 102 時必填',
    },
  }),
  question({
    no: 26,
    groupId: 'qgt-miaolin-sales-ownership',
    type: 'DROPDOWN',
    title: '營一業務',
    required: false,
    specRequired: true,
    customerFieldTarget: 'salesRepDiv1',
    dbFieldName: 'sales_rep_div1',
    dataType: 'VARCHAR(20)',
    choices: [
      '陳曉儀 T219',
      '鄭捷 T283',
      '陳盈穎 T252',
      '陳柏皓 T289',
      '潘彥甫 T339',
      '尹存民 T387',
      '陳采立 T389',
    ],
    settings: {
      ...showWhenNewCustomerAndEquals(24, '營業一課 103'),
      conditionalRequired: '部門別為營業一課 103 時必填',
    },
  }),
  question({
    no: 27,
    groupId: 'qgt-miaolin-sales-ownership',
    type: 'DROPDOWN',
    title: '營二業務',
    required: false,
    specRequired: true,
    customerFieldTarget: 'salesRepDiv2',
    dbFieldName: 'sales_rep_div2',
    dataType: 'VARCHAR(20)',
    choices: ['陳安良 C001', '周怡君 C031', '夏嫚翎 C035', '許浣貽 C042', '林婉茹 C037'],
    settings: {
      ...showWhenNewCustomerAndEquals(24, '營業二課 106'),
      conditionalRequired: '部門別為營業二課 106 時必填',
    },
  }),
  question({
    no: 28,
    groupId: 'qgt-miaolin-sales-ownership',
    type: 'DROPDOWN',
    title: '營三業務',
    required: false,
    specRequired: true,
    customerFieldTarget: 'salesRepDiv3',
    dbFieldName: 'sales_rep_div3',
    dataType: 'VARCHAR(20)',
    choices: ['陳安良 C001', '蔡宛庭 T206', '洪雅雯 T383'],
    settings: {
      ...showWhenNewCustomerAndEquals(24, '營業三課 105'),
      conditionalRequired: '部門別為營業三課 105 時必填',
    },
  }),
  question({
    no: 29,
    groupId: 'qgt-miaolin-sales-ownership',
    type: 'DROPDOWN',
    title: '特通業務',
    required: false,
    specRequired: true,
    customerFieldTarget: 'salesRepSpecialCh',
    dbFieldName: 'sales_rep_special_ch',
    dataType: 'VARCHAR(20)',
    choices: [
      '李丞凱 T245',
      '林文哲 C004',
      '深川真紀 T361',
      '許家瑄 T398',
      '郭家妤 T321',
      '陳錄弦 T357',
      '蕭育豐 T415',
    ],
    settings: {
      ...showWhenNewCustomerAndEquals(24, '特通一課 109'),
      conditionalRequired: '部門別為特通一課 109 時必填',
    },
  }),
  question({
    no: 30,
    groupId: 'qgt-miaolin-sales-ownership',
    type: 'DROPDOWN',
    title: '營四業務',
    required: false,
    specRequired: true,
    customerFieldTarget: 'salesRepDiv4',
    dbFieldName: 'sales_rep_div4',
    dataType: 'VARCHAR(20)',
    choices: ['蔡久喬 T394'],
    settings: {
      ...showWhenNewCustomerAndEquals(24, '營業四課 111'),
      conditionalRequired: '部門別為營業四課 111 時必填',
    },
  }),
  question({
    no: 32,
    groupId: 'qgt-miaolin-customer-classification',
    type: 'MULTIPLE_CHOICE',
    title: '客戶類別',
    required: false,
    customerFieldTarget: 'customerCategory',
    dbFieldName: 'customer_category',
    dataType: 'ENUM',
    choices: ['餐飲', '烘培'],
    settings: { ...showWhenNewCustomer() },
  }),
  question({
    no: 33,
    groupId: 'qgt-miaolin-customer-classification',
    type: 'DROPDOWN',
    title: '客戶通路別',
    required: false,
    customerFieldTarget: 'customerChannel',
    dbFieldName: 'customer_channel',
    dataType: 'VARCHAR(20)',
    choices: customerChannelChoices,
    settings: {
      ...showWhenNewCustomer(),
      dynamicOptions: {
        kind: 'miaolin_channels',
        providerMode: 'webhook',
        sourceLabel: 'Miaolin channels webhook',
        fallback: 'static_question_choices',
      },
    },
  }),
  question({
    no: 34,
    groupId: 'qgt-miaolin-customer-classification',
    type: 'DROPDOWN',
    title: '型態別',
    required: false,
    customerFieldTarget: 'customerType',
    dbFieldName: 'customer_type',
    dataType: 'VARCHAR(30)',
    choices: customerChannelTypeFallbackChoices,
    orderIndex: 33,
    settings: {
      placeholder: '請先選擇客戶通路別',
      dependsOnQuestionId: 'q-miaolin-33',
      dependentSource: 'miaolin_customer_channel',
      dependentChoicesByQuestionValue: customerChannelTypeChoices,
      dependentChoiceEmptyMessage: '請先選擇客戶通路別',
      sharedDbFieldName: 'customer_type',
      ...conditionalDisplayGroups(
        [[workflowChoiceCondition('全新客戶'), { controllerNo: 33, mode: 'show_when_answered' }]],
        'miaolin-channel-type-dependent-question',
      ),
      dynamicOptions: {
        kind: 'miaolin_channel_types',
        providerMode: 'webhook',
        sourceLabel: 'Miaolin channel type webhook',
        controllerQuestionId: 'q-miaolin-33',
        fallback: 'dependent_question_choices',
      },
    },
  }),
  question({
    no: 35,
    groupId: 'qgt-miaolin-region',
    type: 'DROPDOWN',
    title: '客戶區域別',
    customerFieldTarget: 'region',
    dbFieldName: 'region',
    dataType: 'VARCHAR(10)',
    choices: cityChoices,
    orderIndex: 34,
    settings: { ...showWhenNewCustomer() },
  }),
  question({
    no: 36,
    groupId: 'qgt-miaolin-region',
    type: 'DROPDOWN',
    title: '臺北市哪一區？',
    required: false,
    specRequired: true,
    customerFieldTarget: 'districtTaipei',
    dbFieldName: 'district_taipei',
    dataType: 'VARCHAR(10)',
    choices: taipeiDistrictChoices,
    orderIndex: 35,
    settings: {
      ...showWhenNewCustomerAndEquals(35, '臺北市'),
      conditionalRequired: '客戶區域別為臺北市時必填',
    },
  }),
  question({
    no: 37,
    groupId: 'qgt-miaolin-region',
    type: 'DROPDOWN',
    title: '新北市哪一區？',
    required: false,
    specRequired: true,
    customerFieldTarget: 'districtNewTaipei',
    dbFieldName: 'district_newtaipei',
    dataType: 'VARCHAR(10)',
    choices: newTaipeiDistrictChoices,
    orderIndex: 36,
    settings: {
      ...showWhenNewCustomerAndEquals(35, '新北市'),
      conditionalRequired: '客戶區域別為新北市時必填',
    },
  }),
  question({
    no: 38,
    groupId: 'qgt-miaolin-region',
    type: 'DROPDOWN',
    title: '臺中市哪一區？',
    required: false,
    specRequired: true,
    customerFieldTarget: 'districtTaichung',
    dbFieldName: 'district_taichung',
    dataType: 'VARCHAR(10)',
    choices: taichungDistrictChoices,
    orderIndex: 37,
    settings: {
      ...showWhenNewCustomerAndEquals(35, '臺中市'),
      conditionalRequired: '客戶區域別為臺中市時必填',
    },
  }),
  question({
    no: 39,
    groupId: 'qgt-miaolin-region',
    type: 'DROPDOWN',
    title: '臺南市哪一區？',
    required: false,
    specRequired: true,
    customerFieldTarget: 'districtTainan',
    dbFieldName: 'district_tainan',
    dataType: 'VARCHAR(10)',
    choices: tainanDistrictChoices,
    orderIndex: 38,
    settings: {
      ...showWhenNewCustomerAndEquals(35, '臺南市'),
      conditionalRequired: '客戶區域別為臺南市時必填',
    },
  }),
  question({
    no: 40,
    groupId: 'qgt-miaolin-region',
    type: 'DROPDOWN',
    title: '高雄市哪一區？',
    required: false,
    specRequired: true,
    customerFieldTarget: 'districtKaohsiung',
    dbFieldName: 'district_kaohsiung',
    dataType: 'VARCHAR(10)',
    choices: kaohsiungDistrictChoices,
    orderIndex: 39,
    settings: {
      ...showWhenNewCustomerAndEquals(35, '高雄市'),
      conditionalRequired: '客戶區域別為高雄市時必填',
    },
  }),
  question({
    no: 41,
    groupId: 'qgt-miaolin-notes-tracking',
    type: 'LONG_TEXT',
    title: '備註',
    description: "可填寫更改後，原客編是否需停用。EX: A1123 Eddie's Cafe(停用)",
    required: false,
    customerFieldTarget: 'remark',
    dbFieldName: 'remark',
    dataType: 'NVARCHAR(500)',
    orderIndex: 40,
    settings: { ...showWhenNewCustomer(), placeholder: '請輸入備註' },
  }),
  question({
    no: 42,
    groupId: 'qgt-miaolin-notes-tracking',
    type: 'SHORT_TEXT',
    title: '填單人員',
    description: '請填寫人員，資料有問題方便讓建檔人員找尋。',
    customerFieldTarget: 'submittedBy',
    dbFieldName: 'submitted_by',
    dataType: 'NVARCHAR(50)',
    orderIndex: 41,
    settings: { ...showWhenNewCustomer(), placeholder: '填單人員姓名' },
  }),
  question({
    no: 43,
    groupId: 'qgt-miaolin-notes-tracking',
    type: 'SHORT_TEXT',
    title: '食品業者登入字號',
    description:
      '未來銷售資料都需要上傳政府的【非追不可】，故需要請各業務與客戶確認各客戶的食品業者登入字號。',
    required: false,
    customerFieldTarget: 'foodBusinessRegNo',
    dbFieldName: 'food_business_reg_no',
    dataType: 'VARCHAR(50)',
    orderIndex: 42,
    settings: {
      ...showWhenNewCustomerOrExistingChangeTypes(['既有客戶變更統編']),
      placeholder: '食品業者登入字號',
    },
  }),
]

function displayQuestion(no: number): LogicDisplayTarget {
  const questionId = miaolinQuestionId(no)
  const question = sampleQuestions.find((item) => item.id === questionId)
  return {
    type: 'question',
    id: `question:${questionId}`,
    questionId,
    groupId: question?.groupId ?? null,
  }
}

function displayGroup(
  groupId: (typeof miaolinQuestionGroupDefinitions)[number]['id'],
): LogicDisplayTarget {
  return {
    type: 'group',
    id: `group:${groupId}`,
    groupId,
  }
}

function displayGroupQuestion(
  groupId: (typeof miaolinQuestionGroupDefinitions)[number]['id'],
  no: number,
): LogicDisplayTarget {
  const questionId = miaolinQuestionId(no)
  return displayGroupQuestionById(groupId, questionId)
}

function displayGroupQuestionById(
  groupId: (typeof miaolinQuestionGroupDefinitions)[number]['id'],
  questionId: string,
): LogicDisplayTarget {
  return {
    type: 'group_question',
    id: `group_question:${groupId}:${questionId}`,
    groupId,
    questionId,
  }
}

const miaolinNewCustomerBaseTargets: LogicDisplayTarget[] = [
  displayGroupQuestion('qgt-miaolin-source-profile', 4),
  displayGroupQuestion('qgt-miaolin-source-profile', 6),
  displayGroupQuestion('qgt-miaolin-source-profile', 8),
  displayGroupQuestion('qgt-miaolin-source-profile', 14),
  displayGroupQuestion('qgt-miaolin-source-profile', 44),
  displayGroupQuestion('qgt-miaolin-source-profile', 7),
  displayGroup('qgt-miaolin-contact'),
  displayGroup('qgt-miaolin-payment-scale'),
  displayGroup('qgt-miaolin-address'),
  displayGroup('qgt-miaolin-billing-receiving'),
  displayGroupQuestion('qgt-miaolin-sales-ownership', 24),
  displayGroupQuestion('qgt-miaolin-customer-classification', 32),
  displayGroupQuestion('qgt-miaolin-customer-classification', 33),
  displayGroupQuestion('qgt-miaolin-customer-classification', 34),
  displayGroupQuestion('qgt-miaolin-region', 35),
  displayGroup('qgt-miaolin-notes-tracking'),
]

const miaolinExistingChangeBaseTargets: LogicDisplayTarget[] = [
  displayGroupQuestion('qgt-miaolin-workflow', 2),
]

const miaolinExistingChangeCodeTarget: LogicDisplayTarget[] = [
  displayGroupQuestion('qgt-miaolin-workflow', 3),
]

export const sampleLogicJumps: LogicJump[] = [
  {
    id: 'logic-miaolin-new-customer',
    questionId: sampleQuestions[0].id,
    conditions: [{ field: sampleQuestions[0].id, operator: 'equals', value: '全新客戶' }],
    operator: 'AND',
    targetQuestionId: miaolinQuestionId(4),
    displayTargets: miaolinNewCustomerBaseTargets,
    order: 0,
  },
  {
    id: 'logic-miaolin-change-customer',
    questionId: sampleQuestions[0].id,
    conditions: [{ field: sampleQuestions[0].id, operator: 'equals', value: '既有客戶變更資料' }],
    operator: 'AND',
    targetQuestionId: sampleQuestions[1].id,
    displayTargets: miaolinExistingChangeBaseTargets,
    order: 1,
  },
  {
    id: 'logic-miaolin-change-phone',
    questionId: miaolinQuestionId(2),
    conditions: [{ field: miaolinQuestionId(2), operator: 'equals', value: '既有客戶變更電話' }],
    operator: 'AND',
    targetQuestionId: miaolinQuestionId(3),
    displayTargets: [
      ...miaolinExistingChangeCodeTarget,
      displayGroupQuestion('qgt-miaolin-contact', 10),
      displayGroupQuestion('qgt-miaolin-contact', 11),
    ],
    order: 0,
  },
  {
    id: 'logic-miaolin-change-tax-id',
    questionId: miaolinQuestionId(2),
    conditions: [{ field: miaolinQuestionId(2), operator: 'equals', value: '既有客戶變更統編' }],
    operator: 'AND',
    targetQuestionId: miaolinQuestionId(3),
    displayTargets: [
      ...miaolinExistingChangeCodeTarget,
      displayGroupQuestion('qgt-miaolin-source-profile', 14),
      displayGroupQuestion('qgt-miaolin-source-profile', 44),
      displayGroupQuestion('qgt-miaolin-source-profile', 7),
      displayGroupQuestion('qgt-miaolin-notes-tracking', 43),
    ],
    order: 1,
  },
  {
    id: 'logic-miaolin-change-delivery-address',
    questionId: miaolinQuestionId(2),
    conditions: [
      { field: miaolinQuestionId(2), operator: 'equals', value: '既有客戶變更送貨地址' },
    ],
    operator: 'AND',
    targetQuestionId: miaolinQuestionId(3),
    displayTargets: [
      ...miaolinExistingChangeCodeTarget,
      displayGroupQuestion('qgt-miaolin-address', 18),
    ],
    order: 2,
  },
  {
    id: 'logic-miaolin-change-delivery-time',
    questionId: miaolinQuestionId(2),
    conditions: [
      { field: miaolinQuestionId(2), operator: 'equals', value: '既有客戶變更送貨時段' },
    ],
    operator: 'AND',
    targetQuestionId: miaolinQuestionId(3),
    displayTargets: [
      ...miaolinExistingChangeCodeTarget,
      displayGroupQuestion('qgt-miaolin-billing-receiving', 22),
      displayGroupQuestion('qgt-miaolin-billing-receiving', 23),
    ],
    order: 3,
  },
  {
    id: 'logic-miaolin-department-special',
    questionId: miaolinQuestionId(24),
    conditions: [{ field: miaolinQuestionId(24), operator: 'equals', value: '特販一課 102' }],
    operator: 'AND',
    targetQuestionId: miaolinQuestionId(25),
    displayTargets: [displayGroupQuestion('qgt-miaolin-sales-ownership', 25)],
    order: 0,
  },
  {
    id: 'logic-miaolin-department-special-channel',
    questionId: miaolinQuestionId(24),
    conditions: [{ field: miaolinQuestionId(24), operator: 'equals', value: '特通一課 109' }],
    operator: 'AND',
    targetQuestionId: miaolinQuestionId(29),
    displayTargets: [displayGroupQuestion('qgt-miaolin-sales-ownership', 29)],
    order: 1,
  },
  {
    id: 'logic-miaolin-department-sales-1',
    questionId: miaolinQuestionId(24),
    conditions: [{ field: miaolinQuestionId(24), operator: 'equals', value: '營業一課 103' }],
    operator: 'AND',
    targetQuestionId: miaolinQuestionId(26),
    displayTargets: [displayGroupQuestion('qgt-miaolin-sales-ownership', 26)],
    order: 2,
  },
  {
    id: 'logic-miaolin-department-sales-2',
    questionId: miaolinQuestionId(24),
    conditions: [{ field: miaolinQuestionId(24), operator: 'equals', value: '營業二課 106' }],
    operator: 'AND',
    targetQuestionId: miaolinQuestionId(27),
    displayTargets: [displayGroupQuestion('qgt-miaolin-sales-ownership', 27)],
    order: 3,
  },
  {
    id: 'logic-miaolin-department-sales-3',
    questionId: miaolinQuestionId(24),
    conditions: [{ field: miaolinQuestionId(24), operator: 'equals', value: '營業三課 105' }],
    operator: 'AND',
    targetQuestionId: miaolinQuestionId(28),
    displayTargets: [displayGroupQuestion('qgt-miaolin-sales-ownership', 28)],
    order: 4,
  },
  {
    id: 'logic-miaolin-department-sales-4',
    questionId: miaolinQuestionId(24),
    conditions: [{ field: miaolinQuestionId(24), operator: 'equals', value: '營業四課 111' }],
    operator: 'AND',
    targetQuestionId: miaolinQuestionId(30),
    displayTargets: [displayGroupQuestion('qgt-miaolin-sales-ownership', 30)],
    order: 5,
  },
  {
    id: 'logic-miaolin-region-taipei',
    questionId: miaolinQuestionId(35),
    conditions: [{ field: miaolinQuestionId(35), operator: 'equals', value: '臺北市' }],
    operator: 'AND',
    targetQuestionId: miaolinQuestionId(36),
    displayTargets: [displayGroupQuestion('qgt-miaolin-region', 36)],
    order: 0,
  },
  {
    id: 'logic-miaolin-region-new-taipei',
    questionId: miaolinQuestionId(35),
    conditions: [{ field: miaolinQuestionId(35), operator: 'equals', value: '新北市' }],
    operator: 'AND',
    targetQuestionId: miaolinQuestionId(37),
    displayTargets: [displayGroupQuestion('qgt-miaolin-region', 37)],
    order: 1,
  },
  {
    id: 'logic-miaolin-region-taichung',
    questionId: miaolinQuestionId(35),
    conditions: [{ field: miaolinQuestionId(35), operator: 'equals', value: '臺中市' }],
    operator: 'AND',
    targetQuestionId: miaolinQuestionId(38),
    displayTargets: [displayGroupQuestion('qgt-miaolin-region', 38)],
    order: 2,
  },
  {
    id: 'logic-miaolin-region-tainan',
    questionId: miaolinQuestionId(35),
    conditions: [{ field: miaolinQuestionId(35), operator: 'equals', value: '臺南市' }],
    operator: 'AND',
    targetQuestionId: miaolinQuestionId(39),
    displayTargets: [displayGroupQuestion('qgt-miaolin-region', 39)],
    order: 3,
  },
  {
    id: 'logic-miaolin-region-kaohsiung',
    questionId: miaolinQuestionId(35),
    conditions: [{ field: miaolinQuestionId(35), operator: 'equals', value: '高雄市' }],
    operator: 'AND',
    targetQuestionId: miaolinQuestionId(40),
    displayTargets: [displayGroupQuestion('qgt-miaolin-region', 40)],
    order: 4,
  },
]

export const sampleForms: Form[] = [
  {
    id: primaryFormId,
    workspaceId: mockWorkspaceId,
    title: miaolinDemoTitle,
    description: '依照苗林客戶建檔表單規格總表整理的 mock-safe demo 問卷。',
    status: 'published',
    themeConfig: defaultTheme,
    settings: {
      allowResubmit: false,
      maxResponses: DEFAULT_SHARE_MAX_RESPONSES,
      closedAt: null,
      showProgress: true,
      fillMode: 'one_page',
      customerWorkflow: 'customer_onboarding',
      isCustomerIntake: true,
      requiresManagerReview: true,
      autoCreateCustomer: true,
      questionAlign: 'left',
      verticalAlign: 'start',
      answerSpacing: 'comfortable',
      navigationLayout: 'inline',
      progressPosition: 'bottom',
      showQuestionNumber: true,
      showBackButton: true,
      showLogo: false,
      brandName: '苗林實業',
      logoText: 'ML',
      logoAlt: '苗林實業',
      logoPosition: 'top-left',
      logoSize: 32,
      logoShape: 'rounded',
      logoFrame: true,
      showBrandName: true,
      logoLinkUrl: 'https://miao-form.case4.app',
      faviconUrl: '',
      showBrandMark: false,
      brandMarkText: 'Powered by Miao Form',
      brandMarkUrl: 'https://miao-form.case4.app',
      brandMarkPosition: 'bottom-center',
      brandMarkStyle: 'pill',
      brandMarkOpacity: 72,
    },
    welcomeScreen: {
      enabled: false,
      title: '苗林實業客戶資料建檔',
      description: '請依客戶狀態填寫建檔或資料變更資訊；此版本為 mock-safe preview 資料。',
    },
    thankYouScreen: {
      title: '已收到客戶資料',
      description: '資料已在 mock-safe preview 中送出，後續可銜接主管審核流程。',
      animationStyle: 'check_confetti',
      primaryCtaLabel: '回到問卷列表',
      primaryCtaUrl: 'https://miao-form.case4.app/dashboard/forms',
      secondaryCtaLabel: '查看新客申請',
      secondaryCtaUrl: 'https://miao-form.case4.app/dashboard/customer-applications',
      redirectDelaySeconds: 5,
      showPrimaryCta: false,
      showSecondaryCta: false,
      showResubmitButton: false,
      showShareButtons: false,
      showNewSubmissionButton: true,
      newSubmissionButtonLabel: '再次填寫新客戶資料',
    },
    createdBy: mockUserId,
    updatedAt: '2026-05-26T00:00:00.000Z',
  },
]

export const samplePublicForm: PublicForm = {
  ...sampleForms[0],
  questions: sampleQuestions,
  logicJumps: sampleLogicJumps,
  hiddenFields: {},
}

export const sampleResponses: ResponseSummary[] = []

export const sampleDashboardStats: DashboardStats = {
  totalForms: sampleForms.length,
  monthlyResponses: sampleResponses.length,
  averageCompletionRate: 100,
  averageTimeSeconds: 317,
  responseTrend: Array.from({ length: 30 }, (_, index) => ({
    date: `5/${index + 1}`,
    impressions: 20 + index,
    starts: 10 + Math.round(index / 2),
    responses: index % 3 === 0 ? 1 : 0,
  })),
  activeForms: sampleForms.map((form) => ({
    id: form.id,
    title: form.title,
    responses: sampleResponses.filter((response) => response.formId === form.id).length,
    completionRate: 100,
    updatedAt: form.updatedAt,
  })),
  latestResponses: sampleResponses.map((response) => ({
    id: response.id,
    formTitle: miaolinDemoTitle,
    preview: String(
      response.answers['q-miaolin-07'] ?? response.answers['q-miaolin-03'] ?? '已填寫苗林建檔資料',
    ),
    submittedAt: response.submittedAt,
  })),
}

export function getSampleForm(formId = primaryFormId): PublicForm {
  const normalizedFormId = normalizeMiaolinCustomerOnboardingFormId(formId)
  const form = sampleForms.find((item) => item.id === normalizedFormId) ?? sampleForms[0]
  return {
    ...form,
    questions: sampleQuestions,
    logicJumps: sampleLogicJumps,
    hiddenFields: {},
  }
}
